/**
 * FunASR WebSocket 客户端
 * 用于连接自建的 FunASR 语音识别服务
 * 
 * 协议：WebSocket + PCM int16 音频流
 * 参考：A2UI-main/asr_test/mic_asr_test.py
 */

import { FUNASR_CONFIG, getFunASRWebSocketUrl } from '../config/serviceConfig';

class FunASRClient {
  constructor(options = {}) {
    this.wsUrl = options.wsUrl || FUNASR_CONFIG.wsUrl;
    this.sampleRate = options.sampleRate || FUNASR_CONFIG.sampleRate;
    this.blockMs = options.blockMs || FUNASR_CONFIG.blockMs;
    this.itn = options.itn !== undefined ? options.itn : FUNASR_CONFIG.itn;
    // P1：停止后等待最终结果的兜底超时，可配置。
    // 默认 10s 保持原行为；按住说话场景应调小（如 2500ms），避免"识别中"长时间卡住。
    this.stopTimeoutMs = options.stopTimeoutMs || 10000;
    // P0：是否常驻麦克风/AudioContext。开启后 prewarm() 一次拿到麦克风权限并建好采集管线，
    // 之后每次 start() 只需连 WebSocket，onstart 可在 ~100-300ms 内到达，按住说话不再吞前段语音。
    this.keepMicWarm = options.keepMicWarm || false;
    // 预滚动缓冲上限。start() 到 WebSocket 握手完成之间（生产链路可达 1s+）采到的音频
    // 先存进这里，握手一完成立即补发，这样"按下即开口"不会丢句首。
    // 空格键按住说话尤其依赖它：手指已在键上，用户没有鼠标那样的物理前摇。
    this.preRollMs = options.preRollMs || 2000;

    this.ws = null;
    this.audioContext = null;
    this.mediaStream = null;
    this.scriptProcessor = null;
    this.workletNode = null;
    this.workletModuleLoaded = false;
    this.workletModuleContext = null;
    // 采集回调心跳：每次收到音频块（无论是否录音中）都刷新。
    // start() 用它判断常驻管线是否还活着——ScriptProcessor 跑在主线程，
    // 数字人打断等重负载场景下回调可能被饿死，管线看似 prepared 实际已无产出。
    this.lastAudioTickTs = 0;
    // AudioWorklet 在音频线程产出 PCM，但 port.onmessage 仍在主线程执行。
    // 主线程忙于打断多个数字人时，PCM 会排队；若 keyup 的 stop() 先执行，
    // 旧实现会立刻把 isRecording 设为 false，后续才送达的 PCM 被直接丢弃。
    // 用 epoch + Worklet 停止确认把“收口”延后到该批 PCM 全部排空之后。
    this.activeCaptureEpoch = 0;
    this.drainingCaptureEpoch = 0;
    this.stopPendingEpoch = 0;
    this.stopDrainTimer = null;
    this.isRecording = false;
    // 握手期间是否正在收集预滚动音频（仅 start() 到 onstart 之间为 true）
    this.collectingPreRoll = false;
    this.preRollBuffer = [];
    // start() 的重入守卫。isRecording 要等 await 之后才置位，只靠它会让快速连按建出两条 WebSocket。
    this.starting = false;
    // 进行中的预热/采集建立 promise，用于串行化：两次并发建立会留下一条无人引用却仍在
    // 回调的僵尸采集链路，两条链路向同一缓冲送流，服务端收到的音频会片段交错重复。
    this.prewarmPromise = null;
    this.captureStartPromise = null;
    // 本轮是否已经交付过最终识别结果，以及最后一次中间结果（收尾兜底用）
    this.finalDelivered = false;
    this.lastResultText = '';
    // 诊断：本轮已发送的采样数与会话起点。发送音频时长 ≈ 2×按住时长即可断定送流翻倍。
    this.sentSamples = 0;
    this.sessionStartTs = 0;
    this.hasCalledOnend = false;  // 防止重复调用 onend
    this.prepared = false;        // 采集管线（麦克风+AudioContext+processor）是否已就绪
    // 会话编号：每次 start/abort 递增。WebSocket 回调与停止兜底超时按编号守卫，
    // 旧会话的延迟事件（onclose/超时）不会误关新会话——修复"快速点一下后立刻再按无效"。
    this.sessionEpoch = 0;
    // 启动阶段（预热、健康重建、WebSocket 握手）的总等待上限。
    // 采集预热在页面首次进入、数字人预载重负载时可能被主线程饿住；
    // 若无限 await，starting 会一直为 true，后续所有按住说话都会静默被 start() 拦截。
    // 这里宁可明确失败让用户重试，也不能让一条旧会话在数十秒后带着残缺预滚动音频复活。
    this.startTimeoutMs = options.startTimeoutMs || 8000;

    // 回调函数（模拟 SpeechRecognition API）
    this.onstart = null;
    this.onend = null;
    this.onresult = null;
    this.onerror = null;

    // 音频缓冲区（用于收集足够的采样点再发送）
    this.audioBuffer = [];
    this.samplesPerBlock = Math.floor(this.sampleRate * this.blockMs / 1000);
    this.preRollMaxSamples = Math.floor(this.sampleRate * this.preRollMs / 1000);
  }

  /**
   * P0 预热：提前拿到麦克风权限并建好采集管线（不连 WebSocket、不发送音频）。
   * 仅在 keepMicWarm 下生效；可安全重复调用。
   */
  async prewarm() {
    if (!this.keepMicWarm || this.prepared) return;
    // 预热尚未完成时用户就按下说话键很常见（手指本来就在空格上）。
    // 复用同一个 promise，避免 start() 再建一条并行的采集链路。
    if (this.prewarmPromise) return this.prewarmPromise;
    this.prewarmPromise = (async () => {
      try {
        await this._startAudioCapture();
        this.prepared = true;
        console.log('🔥 FunASR: 采集管线已预热（麦克风/AudioContext 就绪）');
      } catch (error) {
        console.warn('FunASR 预热失败（按下时会回退到即时启动）:', error);
        this._stopAudioCapture();
        this.prepared = false;
      } finally {
        this.prewarmPromise = null;
      }
    })();
    return this.prewarmPromise;
  }

  /**
   * 完全释放资源（用于组件卸载）。keepMicWarm 模式下也需显式调用以关闭常驻麦克风。
   */
  dispose() {
    this.isRecording = false;
    this.starting = false;
    this.collectingPreRoll = false;
    this.preRollBuffer = [];
    this.prepared = false;
    this._resetWorkletDrainState();
    this._stopAudioCapture();
    if (this.ws) {
      try { if (this.ws.readyState === WebSocket.OPEN) this.ws.close(); } catch (_) {}
      this.ws = null;
    }
    this.audioBuffer = [];
  }

  /**
   * 开始录音和识别
   */
  async start() {
    if (this.isRecording || this.starting) {
      console.warn('FunASR: 已经在录音中');
      return;
    }
    this.starting = true;
    let epoch = 0;

    try {
      // 重置状态 + 开启新会话编号
      this.hasCalledOnend = false;
      this.finalDelivered = false;
      this.lastResultText = '';
      this.audioBuffer = [];
      this.sentSamples = 0;
      this.sessionStartTs = Date.now();
      epoch = ++this.sessionEpoch;

      // 1. 预热若还在进行中，等它完成再走，绝不另起一条采集链路
      if (this.keepMicWarm && !this.prepared) {
        await this._awaitStartStep(this.prewarm(), epoch, '麦克风/AudioWorklet 预热');
      }
      if (this.sessionEpoch !== epoch) return;

      // 1.5 常驻管线健康自检：麦克风 track 被系统收回、AudioContext 被关、
      //     或采集回调超过 1.5s 没有心跳（主线程重负载饿死 ScriptProcessor），
      //     都说明管线名存实亡——必须拆掉重建，否则本轮只会送出预滚动残留的几百毫秒。
      if (this.prepared && !this._captureAlive()) {
        console.warn('⚠️ FunASR: 常驻采集管线已失效，重建中...');
        this.prepared = false;
        this._stopAudioCapture();
        await this._awaitStartStep(this._startAudioCapture(), epoch, '采集管线重建');
        this.prepared = true;
      }
      if (this.sessionEpoch !== epoch) return;

      // 2. 立即开始收集预滚动音频：此刻用户已经按下按键，随时可能开口，
      //    而 WebSocket 还要几百毫秒才连上。这段音频必须留住。
      this.preRollBuffer = [];
      this.collectingPreRoll = this.prepared;
      this._beginWorkletCapture(epoch);

      // 3. 建立 WebSocket 连接
      await this._awaitStartStep(this._connectWebSocket(epoch), epoch, 'FunASR WebSocket 连接');
      if (this.sessionEpoch !== epoch) return;

      // 4. 采集管线：已预热则直接复用；否则即时建立（兼容未预热/普通模式）
      if (!this.prepared) {
        await this._awaitStartStep(this._startAudioCapture(), epoch, '麦克风采集启动');
        this._beginWorkletCapture(epoch);
        // keepMicWarm 下这条链路会保留复用，语义上等同预热完成。
        // 不置位的话，下一次按下又会重建一条链路，旧链路仍在向同一缓冲送流，
        // 服务端收到的音频片段交错重复——识别结果就是"法国,法国,法国"式的错乱。
        if (this.keepMicWarm) this.prepared = true;
      } else if (this.audioContext && this.audioContext.state === 'suspended') {
        // 预热的 AudioContext 可能因自动播放策略处于 suspended，需在用户手势栈内恢复
        try { await this.audioContext.resume(); } catch (_) {}
      }
      if (this.sessionEpoch !== epoch) return;

      // 5. 补发握手期间攒下的音频，再接实时流，时间轴才是连续的
      this._flushPreRoll();
      this.collectingPreRoll = false;

      this.isRecording = true;
      this.starting = false;
      if (this.onstart) this.onstart();
      console.log('🎤 FunASR: 开始录音');

    } catch (error) {
      console.error('FunASR 启动失败:', error);
      // 使仍在后台运行的 getUserMedia / AudioWorklet / WebSocket await 链彻底失效；
      // 否则它迟到完成后会沿用旧会话的数据，在用户松开很久后才触发 onstart。
      if (this.sessionEpoch === epoch) this.sessionEpoch++;
      this.starting = false;
      this.collectingPreRoll = false;
      this.preRollBuffer = [];
      // 启动失败：关闭本次会话，但 keepMicWarm 下保留已预热的采集管线，便于下次快速启动
      this._endSession(false);
      if (this.onerror) {
        this.onerror({ error: error.message || 'start-failed' });
      }
    }
  }

  /**
   * 启动阶段不能无限等待。Promise.race 只结束本轮 start()；
   * sessionEpoch 在 catch 中递增，后台迟到完成的异步链也不会再复活旧会话。
   */
  _awaitStartStep(promise, epoch, stepName) {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new Error(`${stepName} 超时（${this.startTimeoutMs}ms）`));
      }, this.startTimeoutMs);
      Promise.resolve(promise).then(
        (value) => {
          clearTimeout(timer);
          if (this.sessionEpoch !== epoch) {
            reject(new Error(`${stepName} 已被取消`));
            return;
          }
          resolve(value);
        },
        (error) => {
          clearTimeout(timer);
          reject(error);
        }
      );
    });
  }

  /**
   * 停止录音并等待最终结果
   */
  stop() {
    if (!this.isRecording) {
      console.warn('FunASR: 未在录音中');
      return;
    }

    console.log('🎤 FunASR: 停止录音，等待识别结果...');
    this.isRecording = false;
    const epoch = this.sessionEpoch;

    // Worklet 的 PCM 消息可能已在主线程队列中。先通知音频线程停止打标签；
    // 它回传 capture-stopped 时，代表此前 postMessage 的 PCM 已按序排到主线程，
    // 再统一 flush + 发结束帧，避免丢掉用户松开前说的最后几秒。
    if (this.workletNode && this.activeCaptureEpoch === epoch) {
      this.drainingCaptureEpoch = epoch;
      this.stopPendingEpoch = epoch;
      console.log('⏳ FunASR: 等待 AudioWorklet 排空松开前的 PCM');
      try {
        this.workletNode.port.postMessage({ type: 'capture-stop', epoch });
      } catch (_) {
        this._completeStop(epoch);
        return;
      }
      if (this.stopDrainTimer) clearTimeout(this.stopDrainTimer);
      this.stopDrainTimer = setTimeout(() => {
        // Worklet 确认不应超过数个音频 quantum；5 秒仅为失效节点兜底。
        this._completeStop(epoch);
      }, 5000);
      return;
    }

    this._completeStop(epoch);
  }

  /**
   * Worklet 已排空本次停止前的 PCM（或节点失效兜底）后，才真正结束服务端流。
   */
  _completeStop(epoch) {
    if (epoch !== this.sessionEpoch || this.stopPendingEpoch && this.stopPendingEpoch !== epoch) return;
    if (this.stopDrainTimer) {
      clearTimeout(this.stopDrainTimer);
      this.stopDrainTimer = null;
    }
    this.stopPendingEpoch = 0;
    this.drainingCaptureEpoch = 0;
    console.log('✅ FunASR: AudioWorklet PCM 已排空，发送结束帧');

    // 发送剩余的音频缓冲
    this._flushAudioBuffer();

    // 发送结束帧
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const endMsg = { is_speaking: false, is_finished: true };
      this.ws.send(JSON.stringify(endMsg));
      console.log('🎤 FunASR: 已发送结束帧');
    }

    // 诊断：发送音频时长应约等于本轮会话时长（按住 + 握手）。
    // 若约为其 2 倍，说明客户端送流翻倍（存在并行采集链路）；比值正常但识别仍重复，则问题在服务端。
    const audioMs = Math.round(this.sentSamples / this.sampleRate * 1000);
    const sessionMs = this.sessionStartTs ? (Date.now() - this.sessionStartTs) : 0;
    console.log(`📊 FunASR 诊断: 本轮发送音频 ${audioMs}ms / 会话时长 ${sessionMs}ms / 比值 ${sessionMs ? (audioMs / sessionMs).toFixed(2) : '-'}`);

    // 停止向服务端发送音频：keepMicWarm 下保留采集管线常驻，否则停止采集。
    // WebSocket 仍保持，等待最终识别结果。
    if (!this.keepMicWarm) {
      this._stopAudioCapture();
    }
    this.audioBuffer = [];

    // 超时保护：超过 stopTimeoutMs 仍无结果则强制结束（按住说话场景调小，避免长时间"识别中"）
    setTimeout(() => {
      // 已开启新会话则不再处理旧会话的超时，避免误关新会话
      if (this.sessionEpoch !== epoch || this.hasCalledOnend) return;
      console.warn(`FunASR: 等待结果超时(${this.stopTimeoutMs}ms)，强制结束`);
      this._endSession(true);
    }, this.stopTimeoutMs);
  }

  /**
   * 立即中止当前会话（用于"短按取消"）：递增会话编号使旧回调失效，关闭 WebSocket，
   * 停止送流。不触发 onend（由调用方自行复位 UI），keepMicWarm 下保留采集管线常驻。
   * 与 stop() 的区别：不保留 ws 空等结果，因此紧接着再次 start() 不会被旧会话干扰。
   */
  abort() {
    this.sessionEpoch++;
    this.isRecording = false;
    this.starting = false;
    this.collectingPreRoll = false;
    this.preRollBuffer = [];
    this.hasCalledOnend = true;
    this._resetWorkletDrainState();
    if (this.ws) {
      try { if (this.ws.readyState === WebSocket.OPEN) this.ws.close(); } catch (_) {}
      this.ws = null;
    }
    if (!this.keepMicWarm) {
      this._stopAudioCapture();
    }
    this.audioBuffer = [];
  }

  /**
   * 建立 WebSocket 连接
   * HTTPS环境下会自动将相对路径转换为完整的WSS URL
   */
  _connectWebSocket(epoch) {
    return new Promise((resolve, reject) => {
      try {
        // 使用辅助函数获取完整的WebSocket URL（支持HTTPS环境）
        const wsUrl = getFunASRWebSocketUrl();
        console.log('🔗 FunASR 连接URL:', wsUrl);
        // 用局部 ws 变量，配合 sessionEpoch 守卫：旧会话的回调不会操作新会话的 this.ws
        const ws = new WebSocket(wsUrl);
        this.ws = ws;

        const timeout = setTimeout(() => {
          reject(new Error('WebSocket 连接超时'));
          try { ws.close(); } catch (_) {}
        }, 10000);

        ws.onopen = () => {
          clearTimeout(timeout);
          // 连接成功前已被新会话取代，直接丢弃这个连接
          if (this.sessionEpoch !== epoch) { try { ws.close(); } catch (_) {} return; }
          console.log('🔗 FunASR WebSocket 已连接');

          // 发送初始化消息
          const initMsg = {
            wav_name: `web-${Date.now()}`,
            wav_format: 'pcm',
            audio_fs: this.sampleRate,
            itn: this.itn,
          };
          ws.send(JSON.stringify(initMsg));
          console.log('📤 FunASR: 已发送初始化消息');
          resolve();
        };

        ws.onmessage = (event) => {
          if (this.sessionEpoch !== epoch) return;  // 忽略旧会话消息
          this._handleMessage(event.data);
        };

        ws.onerror = (error) => {
          clearTimeout(timeout);
          if (this.sessionEpoch !== epoch) return;
          console.error('FunASR WebSocket 错误:', error);
          reject(new Error('WebSocket 连接失败'));
        };

        ws.onclose = () => {
          console.log('🔗 FunASR WebSocket 已关闭');
          // 旧会话的关闭事件不得收尾新会话
          if (this.sessionEpoch !== epoch) return;
          // 确保 onend 被调用（无论 isRecording 状态）；keepMicWarm 下保留采集管线
          this._endSession(true);
        };

      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * 处理服务端返回的消息
   */
  _handleMessage(data) {
    try {
      const msg = JSON.parse(data);
      const text = msg.text || '';
      const isFinal = msg.is_final;
      const mode = msg.mode;

      console.log(`📝 FunASR 结果: mode=${mode} is_final=${isFinal} text="${text}"`);

      // 2pass 模式下服务端先连续回若干 online 流式碎片，最后才回 offline 整句修正结果。
      // 碎片出自流式模型，本身就不准且带重复，必须标记出来，绝不能当成一次完整发言。
      const isFinalResult = isFinal === true || String(mode || '').includes('offline');

      if (text && this.onresult) {
        this.lastResultText = text;
        if (isFinalResult) this.finalDelivered = true;
        // 构造类似 SpeechRecognition 的 event 对象
        const event = {
          results: [[{ transcript: text, confidence: 1.0 }]],
          resultIndex: 0,
          isFinal: isFinalResult,
        };
        this.onresult(event);
      }

      if (isFinalResult && text) {
        this._endSession(true);
      }

    } catch (error) {
      console.error('FunASR 解析消息失败:', error, data);
    }
  }

  /**
   * 结束一次识别会话：关闭 WebSocket；keepMicWarm 下保留麦克风/AudioContext 常驻，
   * 否则停止采集。callOnend 时触发一次 onend（幂等，靠 hasCalledOnend 防重复）。
   */
  _endSession(callOnend) {
    // 收尾时若始终没等到最终结果（服务端只回了流式碎片，或等待超时），
    // 用最后一次碎片补一个最终结果，避免这一轮发言凭空消失。
    if (callOnend && !this.finalDelivered && this.lastResultText && this.onresult) {
      this.finalDelivered = true;
      console.warn('FunASR: 未收到最终结果，以最后一次中间结果收尾');
      this.onresult({
        results: [[{ transcript: this.lastResultText, confidence: 1.0 }]],
        resultIndex: 0,
        isFinal: true,
      });
    }
    this.isRecording = false;
    this.starting = false;
    this.collectingPreRoll = false;
    this.preRollBuffer = [];
    this._resetWorkletDrainState();
    if (this.ws) {
      try { if (this.ws.readyState === WebSocket.OPEN) this.ws.close(); } catch (_) {}
      this.ws = null;
    }
    if (!this.keepMicWarm) {
      this._stopAudioCapture();
    }
    this.audioBuffer = [];
    if (callOnend && !this.hasCalledOnend) {
      this.hasCalledOnend = true;
      if (this.onend) this.onend();
    }
  }

  /**
   * 开始音频采集。幂等且并发安全：同一实例绝不允许两条采集链路并存。
   * 旧链路一旦被新引用覆盖，仍会持续回调并向共享的 audioBuffer 送流，
   * 服务端收到的就是两路交错重复的音频。
   */
  _startAudioCapture() {
    if (this.captureStartPromise) return this.captureStartPromise;
    this.captureStartPromise = this._openAudioCapture().finally(() => {
      this.captureStartPromise = null;
    });
    return this.captureStartPromise;
  }

  /**
   * 常驻采集管线是否还活着：track 未被系统收回、AudioContext 未关闭、
   * 且采集回调心跳新鲜（正常情况下每 ~128-256ms 一次）。
   */
  _captureAlive() {
    const track = this.mediaStream && this.mediaStream.getTracks
      ? this.mediaStream.getTracks()[0] : null;
    if (!track || track.readyState === 'ended') return false;
    if (!this.audioContext || this.audioContext.state === 'closed') return false;
    if (this.lastAudioTickTs && Date.now() - this.lastAudioTickTs > 1500) return false;
    return true;
  }

  _beginWorkletCapture(epoch) {
    this.activeCaptureEpoch = epoch;
    this.drainingCaptureEpoch = 0;
    try {
      this.workletNode?.port.postMessage({ type: 'capture-start', epoch });
    } catch (_) {}
  }

  _resetWorkletDrainState() {
    if (this.stopDrainTimer) {
      clearTimeout(this.stopDrainTimer);
      this.stopDrainTimer = null;
    }
    this.activeCaptureEpoch = 0;
    this.drainingCaptureEpoch = 0;
    this.stopPendingEpoch = 0;
  }

  async _openAudioCapture() {
    // 先拆掉可能残留的旧链路，再建新的
    this._stopAudioCapture();

    // 获取麦克风权限
    this.mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        sampleRate: this.sampleRate,
        echoCancellation: true,
        noiseSuppression: true,
      }
    });
    // 麦克风被系统/其他应用收回时 track 直接结束，标记管线失效，下次 start() 自动重建
    const track = this.mediaStream.getTracks()[0];
    if (track) {
      track.onended = () => {
        console.warn('⚠️ FunASR: 麦克风 track 已被结束（可能被系统或其他应用抢占）');
        this.prepared = false;
      };
    }

    // 创建 AudioContext
    this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
      sampleRate: this.sampleRate,
    });

    const source = this.audioContext.createMediaStreamSource(this.mediaStream);
    this.lastAudioTickTs = Date.now();

    // 优先 AudioWorklet：采集跑在独立音频线程，主线程再忙（数字人打断时要同时处理
    // TTS 段丢弃、blendshapes、渲染）音频也不会断流。消息经 port 排队送回主线程处理，
    // 主线程短暂阻塞只会延迟发送，不会丢数据。
    // ScriptProcessor 跑在主线程，重负载下回调会被饿死——曾导致按住数秒只送出预滚动的
    // 几百毫秒残留、识别结果为空。仅在 AudioWorklet 不可用/被 CSP 拦截时回退使用。
    let workletOk = false;
    if (this.audioContext.audioWorklet) {
      try {
        await this._ensureWorkletModule();
        this.workletNode = new AudioWorkletNode(this.audioContext, 'funasr-pcm-forwarder', {
          numberOfInputs: 1,
          numberOfOutputs: 1,
          channelCount: 1,
        });
        this.workletNode.port.onmessage = (event) => {
          const message = event.data;
          if (message?.type === 'capture-stopped') {
            if (message.epoch === this.stopPendingEpoch) {
              this._completeStop(message.epoch);
            }
            return;
          }
          if (message?.type === 'tick') {
            this.lastAudioTickTs = Date.now();
            return;
          }
          // 新版 Worklet 带 capture epoch；保留 Float32 回退以兼容热更新前已创建的节点。
          this._onAudioChunk(message?.data || message, message?.epoch || 0);
        };
        source.connect(this.workletNode);
        this.workletNode.connect(this.audioContext.destination);
        workletOk = true;
        console.log('🎧 FunASR: 采集使用 AudioWorklet（音频线程，抗主线程阻塞）');
      } catch (error) {
        console.warn('FunASR: AudioWorklet 不可用，回退 ScriptProcessor:', error);
        this.workletNode = null;
      }
    }

    if (!workletOk) {
      const bufferSize = 4096;
      this.scriptProcessor = this.audioContext.createScriptProcessor(bufferSize, 1, 1);
      this.scriptProcessor.onaudioprocess = (event) => {
        this._onAudioChunk(event.inputBuffer.getChannelData(0));
      };
      source.connect(this.scriptProcessor);
      this.scriptProcessor.connect(this.audioContext.destination);
    }
  }

  /**
   * 注册 AudioWorklet 模块（每个 AudioContext 只需一次）。
   * 用 Blob URL 内联，免去静态文件路径/部署配置。
   */
  _ensureWorkletModule() {
    if (this.workletModuleLoaded && this.workletModuleContext === this.audioContext) {
      return Promise.resolve();
    }
    const code = `
      class FunASRPCMForwarder extends AudioWorkletProcessor {
        constructor() {
          super();
          this.captureEpoch = 0;
          this.heartbeatFrames = 0;
          this.port.onmessage = (event) => {
            const message = event.data || {};
            if (message.type === 'capture-start') {
              this.captureEpoch = message.epoch || 0;
            } else if (message.type === 'capture-stop' && message.epoch === this.captureEpoch) {
              // 同一个 MessagePort 从 Worklet → 主线程的消息严格有序。
              // 因此该确认之前的所有 PCM 都已排在队列中，主线程收到确认后再发结束帧。
              this.captureEpoch = 0;
              this.port.postMessage({ type: 'capture-stopped', epoch: message.epoch });
            }
          };
        }
        process(inputs) {
          const ch = inputs[0] && inputs[0][0];
          if (ch && ch.length && this.captureEpoch) {
            const copy = new Float32Array(ch.length);
            copy.set(ch);
            this.port.postMessage({ type: 'pcm', epoch: this.captureEpoch, data: copy }, [copy.buffer]);
          } else if (++this.heartbeatFrames >= 125) {
            // 非录音期只需 1 秒一次健康心跳；不把 125Hz PCM 无意义地压到主线程。
            this.heartbeatFrames = 0;
            this.port.postMessage({ type: 'tick' });
          }
          return true;
        }
      }
      registerProcessor('funasr-pcm-forwarder', FunASRPCMForwarder);
    `;
    const blobUrl = URL.createObjectURL(new Blob([code], { type: 'application/javascript' }));
    return this.audioContext.audioWorklet.addModule(blobUrl).then(() => {
      this.workletModuleLoaded = true;
      this.workletModuleContext = this.audioContext;
    }).finally(() => {
      URL.revokeObjectURL(blobUrl);
    });
  }

  /**
   * 处理一块采集到的 Float32 音频（AudioWorklet 与 ScriptProcessor 共用）。
   */
  _onAudioChunk(inputData, captureEpoch = 0) {
    this.lastAudioTickTs = Date.now();
    // stop() 后仍要接收在 Worklet 停止确认前已经生成、但因主线程繁忙而排队的 PCM。
    // 不能无条件接收：captureEpoch=0 是非录音期的健康心跳，必须丢弃。
    const isCurrentCapture = captureEpoch && captureEpoch === this.activeCaptureEpoch;
    const isDrainingCapture = captureEpoch && captureEpoch === this.drainingCaptureEpoch;
    if (!this.isRecording && !this.collectingPreRoll && !isDrainingCapture) return;
    if (captureEpoch && !isCurrentCapture && !isDrainingCapture) return;

    // 如果需要重采样（浏览器实际采样率可能不是 16kHz）
    const actualSampleRate = this.audioContext ? this.audioContext.sampleRate : this.sampleRate;
    let processedData = inputData;
    if (actualSampleRate !== this.sampleRate) {
      processedData = this._resample(inputData, actualSampleRate, this.sampleRate);
    }

    // WebSocket 还没连上：先攒着，连上后由 _flushPreRoll 补发
    if (!this.isRecording && !isDrainingCapture) {
      for (let i = 0; i < processedData.length; i++) {
        this.preRollBuffer.push(processedData[i]);
      }
      const overflow = this.preRollBuffer.length - this.preRollMaxSamples;
      if (overflow > 0) this.preRollBuffer.splice(0, overflow);
      return;
    }

    // 添加到缓冲区
    for (let i = 0; i < processedData.length; i++) {
      this.audioBuffer.push(processedData[i]);
    }

    // 当缓冲区达到一个 block 的大小时，发送数据
    while (this.audioBuffer.length >= this.samplesPerBlock) {
      const block = this.audioBuffer.splice(0, this.samplesPerBlock);
      this._sendAudioBlock(block);
    }
  }

  /**
   * 发送音频块（转换为 PCM int16）
   */
  _sendAudioBlock(floatData) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

    // Float32 [-1, 1] -> Int16 [-32768, 32767]
    const int16Data = new Int16Array(floatData.length);
    for (let i = 0; i < floatData.length; i++) {
      const s = Math.max(-1, Math.min(1, floatData[i]));
      int16Data[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }

    this.sentSamples += int16Data.length;
    this.ws.send(int16Data.buffer);
  }

  /**
   * 补发握手期间攒下的预滚动音频。按 block 切分发送，保持与实时流一致的分片粒度。
   */
  _flushPreRoll() {
    const preRoll = this.preRollBuffer;
    this.preRollBuffer = [];
    if (!preRoll.length) return;
    while (preRoll.length >= this.samplesPerBlock) {
      this._sendAudioBlock(preRoll.splice(0, this.samplesPerBlock));
    }
    // 不足一个 block 的尾巴并入实时缓冲，避免丢掉几十毫秒
    if (preRoll.length) this.audioBuffer = preRoll;
    console.log(`⏪ FunASR: 已补发握手期间的预滚动音频`);
  }

  /**
   * 发送剩余的音频缓冲
   */
  _flushAudioBuffer() {
    if (this.audioBuffer.length > 0) {
      this._sendAudioBlock(this.audioBuffer);
      this.audioBuffer = [];
    }
  }

  /**
   * 简单的线性重采样
   */
  _resample(inputData, fromRate, toRate) {
    if (fromRate === toRate) return inputData;

    const ratio = fromRate / toRate;
    const outputLength = Math.floor(inputData.length / ratio);
    const output = new Float32Array(outputLength);

    for (let i = 0; i < outputLength; i++) {
      const srcIndex = i * ratio;
      const srcIndexFloor = Math.floor(srcIndex);
      const srcIndexCeil = Math.min(srcIndexFloor + 1, inputData.length - 1);
      const t = srcIndex - srcIndexFloor;
      output[i] = inputData[srcIndexFloor] * (1 - t) + inputData[srcIndexCeil] * t;
    }

    return output;
  }

  /**
   * 停止音频采集
   */
  _stopAudioCapture() {
    if (this.workletNode) {
      try { this.workletNode.port.onmessage = null; } catch (_) {}
      try { this.workletNode.disconnect(); } catch (_) {}
      this.workletNode = null;
    }
    if (this.scriptProcessor) {
      this.scriptProcessor.disconnect();
      this.scriptProcessor = null;
    }
    this.workletModuleLoaded = false;
    this.workletModuleContext = null;
    this.lastAudioTickTs = 0;

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }

    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
      this.audioContext = null;
    }
  }

}

export default FunASRClient;

